import { Box, Button, Stack, Typography } from '@mui/material';
import ContactPersonnelProfile from './ContactPersonnelProfile';
import ContactPersonnelList from './ContactPersonnelList';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import {
  getCommunityMembers,
  getCustomFieldConfigs,
  getExperts,
  getJournalists,
  getOrganizations,
  getOrgs,
} from './contactsRequest';
import ContactCategorySelect from './ContactCategorySelect';
import OrgList from './OrgList';
import OrgProfile from './OrgProfile';
import { useUnits } from './Hooks/useUnits';
import ContactFuzzySearch from './ContactFuzzySearch';
import CotactsExportPreview from './ContactsExportPreview';
import AddIcon from '@mui/icons-material/Add';
import CombinedContactPersonnelModal from './Modal/CombinedContactPersonnelModal';
import ContactsPageAddOutletModal from './Modal/ContactsPageAddOutletModal';
import { useJournalists } from './Hooks/useJournalists';
import { useCommunityMembers } from './Hooks/useCommunityMembers';
import { useQuery } from '@tanstack/react-query';

const useStyles = makeStyles((theme) => ({
  contactPageContainer: {
    margin: '0',
    padding: '0',
    width: '100vw',
    height: '100vh',
  },
  contactHeader: {
    width: '150px',
    marginLeft: '0',
    marginBottom: '0',
  },
  contactOperations: {
    marginTop: '20px',
    marginLeft: '0',
    marginBottom: '0',
  },
  outerContainer: {
    width: '100%',
    height: '100%',
    paddingTop: '0rem',
    marginLeft: '0',
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  contactsListContainer: {
    height: '80vh',
    borderRadius: '1rem',
    marginLeft: '0',
  },
  contactsProfileContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '80vh',
    width: '60vw',
    background: '#e8eef5',
    borderTopRightRadius: '1rem',
    borderBottomRightRadius: '1rem',
  },
  spinnerContainer: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

const ContactsPage = ({ dropdownTable }) => {
  const classes = useStyles();
  const [searchedContacts, setSearchedContacts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [refreshOrgs, setRefreshOrgs] = useState(false);
  const [refreshContacts, setRefreshContacts] = useState(false);
  const [query, setQuery] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [openAddContact, setOpenAddContact] = useState(false);
  const [category, setCategory] = useState('Journalists');
  const isContactPersonnel = category === 'Journalists' || category === 'Experts' || category === 'Community Members';

  const { data: units, isLoading: isLoadingUnits, isError } = useUnits();

  const expertCustomFieldsQuery = useQuery({
    queryKey: ['expertCustomFieldConfigs', 'contactPage'],
    queryFn: () => getCustomFieldConfigs('Expert'),
  });

  const journalistCustomFieldsQuery = useQuery({
    queryKey: ['journalistCustomFieldConfigs', 'contactPage'],
    queryFn: () => getCustomFieldConfigs('Journalist'),
  });

  const outletCustomFieldsQuery = useQuery({
    queryKey: ['outletCustomFieldConfigs', 'contactPage'],
    queryFn: () => getCustomFieldConfigs('Org'),
  });

  const communityMemberCustomFieldsQuery = useQuery({
    queryKey: ['communityMemberCustomFieldConfigs', 'contactPage'],
    queryFn: () => getCustomFieldConfigs('CommunityMember'),
  });

  const organizationCustomFieldsQuery = useQuery({
    queryKey: ['organizationCustomFieldConfigs', 'contactPage'],
    queryFn: () => getCustomFieldConfigs('Organization'),
  });

  function getContactsByOrganizations(organizations, contactPersonnels, orgType) {
    // Create a set of outlet IDs
    const organizationIDs = new Set(organizations.map((organization) => organization?._id));

    if (!Array.isArray(contactPersonnels.data)) {
      console.error('contactPersonnels input is not an array:', contactPersonnels);
      return [];
    }

    // Filter the journalists to only include those who belong to the given outlets
    return contactPersonnels.data?.filter((contactPersonnel) => {
      return organizationIDs.has(contactPersonnel?.[orgType]?._id);
    });
  }

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
  };
  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
    setRefreshContacts(true);
    setRefreshOrgs(true);
    setSelectedContact(null);
    setSearchedContacts([]);
    setContacts([]);
    setQuery('');
  };

  const getCustomFieldConfigsDataByCategory = (category) => {
    switch (category) {
      case 'Journalists':
        return journalistCustomFieldsQuery.data;
      case 'Experts':
        return expertCustomFieldsQuery.data;
      case 'Outlets':
        return outletCustomFieldsQuery.data;
      case 'Community Members':
        return communityMemberCustomFieldsQuery.data;
      case 'Organizations':
        return organizationCustomFieldsQuery.data;
      default:
        return [];
    }
  };

  const getFuzzySearchCustomFieldConfigsData = () => {
    return getCustomFieldConfigsDataByCategory(category);
  };

  const getContactPersonnelCustomFieldConfigsData = () => {
    switch (category) {
      case 'Journalists':
        return journalistCustomFieldsQuery.data;
      case 'Experts':
        return expertCustomFieldsQuery.data;
      case 'Community Members':
        return communityMemberCustomFieldsQuery.data;
      default:
        return [];
    }
  };

  const getContactOrgCustomFieldConfigsData = () => {
    switch (category) {
      case 'Outlets':
        return outletCustomFieldsQuery.data;
      case 'Organizations':
        return organizationCustomFieldsQuery.data;
      default:
        return [];
    }
  };

  // Used when exporting organization
  const {
    data: journalists,
    isLoading: isLoadingJournalists,
    isSuccess: isSuccessJournalists,
    isError: isErrorJournalists,
  } = useJournalists();
  const {
    data: communityMembers,
    isLoading: isLoadingMembers,
    isSuccess: isSuccessMembers,
    isError: isErrorMembers,
  } = useCommunityMembers();

  const contactsQuery = useQuery({
    queryKey: ['contacts', category, refreshContacts],
    queryFn: () => {
      switch (category) {
        case 'Journalists':
          return getJournalists();
        case 'Experts':
          return getExperts();
        case 'Outlets':
          return getOrgs();
        case 'Community Members':
          return getCommunityMembers();
        case 'Organizations':
          return getOrganizations();
        default:
          console.error('invalid category provided in contacts page');
          return Promise.resolve({ data: [] });
      }
    },
    onError: (err) => {
      console.log('Error getting contacts: ', err);
    },
  });

  const orgsQuery = useQuery({
    queryKey: ['contactOrgs', category, refreshOrgs],
    queryFn: () => {
      switch (category) {
        case 'Journalists':
        case 'Experts':
        case 'Outlets':
          return getOrgs();
        case 'Community Members':
        case 'Organizations':
          return getOrganizations();
        default:
          console.error('invalid category provided in contacts page');
          return Promise.resolve({ data: [] });
      }
    },
    onError: (err) => {
      console.log('Error getting organizations: ', err);
    },
  });

  useEffect(() => {
    setIsLoadingList(true);
  }, [refreshContacts, category]);

  useEffect(() => {
    if (!contactsQuery.data) {
      return;
    }
    let responseData = [];
    if (Array.isArray(contactsQuery.data?.data)) {
      responseData = contactsQuery.data.data;
    }
    const sortedResponseData = responseData.sort((a, b) => {
      return a.name.trim().localeCompare(b.name.trim());
    });

    setContacts(sortedResponseData);

    if (query === '') {
      setSearchedContacts(sortedResponseData);
    }

    setSelectedContact((previousSelected) => {
      if (!sortedResponseData.length) {
        return null;
      }
      if (!previousSelected) {
        return sortedResponseData[0];
      }
      const updatedSelectedContact = sortedResponseData.find((contact) => previousSelected._id === contact?._id);
      return updatedSelectedContact || previousSelected;
    });

    setIsLoadingList(false);
    setRefreshContacts(false);
  }, [contactsQuery.data, query]);

  useEffect(() => {
    if (!orgsQuery.data) {
      return;
    }
    let orgsData = [];
    if (Array.isArray(orgsQuery.data?.data)) {
      orgsData = orgsQuery.data.data;
    }
    setOrgs(orgsData);
    setRefreshOrgs(false);
  }, [orgsQuery.data]);

  if (isLoadingUnits || isLoadingList || isLoadingJournalists || isLoadingMembers)
    return <div className={classes.spinnerContainer}>Loading...</div>;

  if (isError) {
    return (
      <div className={classes.spinnerContainer}>
        <Typography variant="h3">Error loading users or units</Typography>
        <Typography variant="h5">Please refresh the page</Typography>
      </div>
    );
  }

  let contactsList = null;
  let contactDetails = null;
  let contactAddModal = null;
  let contactExportPreview = null;
  if (isContactPersonnel) {
    contactsList = (
      <ContactPersonnelList
        data={searchedContacts}
        handleContactClick={handleContactClick}
        category={category}
        isLoadingList={isLoadingList}
        selectedContact={selectedContact}
        units={units}
        style={{
          borderTopLeftRadius: '1rem',
          borderBottomLeftRadius: '1rem',
          borderRightStyle: 'none',
        }}
      />
    );
    contactDetails = (
      <Box className={classes.contactsProfileContainer}>
        <ContactPersonnelProfile
          selectedContact={selectedContact}
          category={category}
          units={units}
          orgs={orgs}
          setRefreshContacts={setRefreshContacts}
          setRefreshOrgs={setRefreshOrgs}
          customFieldConfigs={getContactPersonnelCustomFieldConfigsData()}
        />
      </Box>
    );
    contactAddModal = (
      <CombinedContactPersonnelModal
        openAddContact={openAddContact}
        setOpenAddContact={setOpenAddContact}
        orgs={orgs}
        units={units}
        setRefreshContacts={setRefreshContacts}
        setRefreshOrgs={setRefreshOrgs}
        category={category}
        isInContactPage={true}
        contacts={searchedContacts}
      />
    );
    contactExportPreview = (
      <CotactsExportPreview
        dropdownTable={dropdownTable}
        category={category}
        previewData={searchedContacts}
        onPreviewClose={() => {}}
      />
    );
  } else {
    contactsList = (
      <OrgList
        selectedOrganization={selectedContact}
        organizations={searchedContacts}
        handleContactClick={handleContactClick}
        isLoadingList={isLoadingList}
        style={{
          borderTopLeftRadius: '1rem',
          borderBottomLeftRadius: '1rem',
          borderRightStyle: 'none',
        }}
      />
    );
    contactDetails = (
      <Box className={classes.contactsProfileContainer}>
        <OrgProfile
          selectedOrg={selectedContact}
          setRefreshContacts={setRefreshContacts}
          category={category}
          customFieldConfigs={getContactOrgCustomFieldConfigsData()}
        />
      </Box>
    );
    contactAddModal = (
      <ContactsPageAddOutletModal
        openModal={openAddContact}
        setOpenModal={setOpenAddContact}
        setRefreshContacts={setRefreshContacts}
        category={category}
      />
    );
    contactExportPreview = (
      <>
        {isErrorJournalists && isErrorMembers && <Typography>Error fetching contacts</Typography>}
        {isSuccessJournalists && isSuccessMembers && (
          <CotactsExportPreview
            dropdownTable={dropdownTable}
            category={category}
            previewData={getContactsByOrganizations(
              searchedContacts,
              category === 'Outlets' ? journalists : communityMembers,
              category === 'Outlets' ? 'Org' : 'Organization'
            )}
            onPreviewClose={() => {}}
          />
        )}
      </>
    );
  }

  return (
    <Stack direction="column" alignItems="center" className={classes.contactPageContainer}>
      <Stack direction="column">
        <Stack direction="row" justifyContent="space-between" className={classes.contactOperations}>
          <Box>
            <ContactCategorySelect category={category} handleCategoryChange={handleCategoryChange} />
            <ContactFuzzySearch
              contacts={contacts}
              setSearchResults={setSearchedContacts}
              query={query}
              setQuery={setQuery}
              category={category}
              units={units}
              customFieldConfigs={getFuzzySearchCustomFieldConfigsData()}
            />
          </Box>

          <Box>
            <Button
              sx={{ marginRight: '10px' }}
              variant="contained"
              onClick={() => {
                setOpenAddContact(true);
              }}
              data-cy="addContact-button-ContactsPage"
            >
              <AddIcon />
              Add {category.slice(0, -1)}
            </Button>
            {contactExportPreview}
          </Box>
        </Stack>
        <Stack direction="row" alignItems="start" className={classes.outerContainer}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            className={classes.contactsListContainer}
          >
            {contactsList}
          </Stack>
          {contactDetails}
        </Stack>
      </Stack>
      {contactAddModal}
    </Stack>
  );
};

export default ContactsPage;
